import { useContext, useMemo } from 'react';
import { AppContext, Pairs } from '@/store/appContext';
import { Currency } from '@Apollo/types';

type CurrencyRates = {
  [key in Pairs]: number;
};

type ConversionItem = {
  from: Currency;
  to: Currency;
  amount: number;
};

type ConversionItemWithPair = {
  pair: Pairs;
  amount: number;
};

type ReturnType = {
  exchangedList: ConversionItemWithPair[];
  exchangedDict: Record<Pairs, number>;
};

function roundToFixed(number: number, decimals: number = 5): number {
  const roundedStr = number.toFixed(decimals);
  return parseFloat(roundedStr);
}

function tryConvertViaIntermediate(
  from: Currency,
  to: Currency,
  amount: number,
  currencyRates: CurrencyRates,
  intermediate: Currency,
): number | null {
  // Ищем from -> intermediate
  const fromToIntermediate = `${from}_${intermediate}` as Pairs;
  // Ищем intermediate -> to
  const intermediateTo = `${intermediate}_${to}` as Pairs;

  if (fromToIntermediate in currencyRates && intermediateTo in currencyRates) {
    return amount * currencyRates[fromToIntermediate] * currencyRates[intermediateTo];
  }

  return null;
}

export function useConvertCurrencies(items: ConversionItem[]): ReturnType {
  const currencyRates = useContext(AppContext).currencyRates as CurrencyRates;

  return useMemo(() => {
    const extendedItems = [...items, ...items.map(({ from, amount }) => ({ from, to: Currency.Usdt, amount }))];
    const result = extendedItems.map(({ from, to, amount }) => {
      const directPair = `${from}_${to}` as Pairs;
      if (directPair in currencyRates) {
        // Прямой курс найден
        return { pair: directPair, amount: roundToFixed(amount * currencyRates[directPair]) };
      }

      // Пробуем через USDT
      let converted = tryConvertViaIntermediate(from, to, amount, currencyRates, Currency.Usdt);
      if (converted !== null) {
        return { pair: directPair, amount: roundToFixed(converted) };
      }

      // Пробуем через USD
      converted = tryConvertViaIntermediate(from, to, amount, currencyRates, Currency.Usd);
      if (converted !== null) {
        return { pair: directPair, amount: roundToFixed(converted) };
      }

      // Если ни прямого курса, ни цепочки нет - возвращаем 0
      return { pair: directPair, amount: roundToFixed(amount) };
    });

    const dictionary = result.reduce((acc, { pair, amount }) => {
      acc[pair] = amount;
      return acc;
    }, {} as Record<Pairs, number>);

    return {
      exchangedList: result,
      exchangedDict: dictionary,
    };
  }, [currencyRates, items]);
}
